import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Numbered pagination control.
 * Renders: « ‹ 1 … 4 5 [6] 7 8 … 12 › »
 *
 * Props:
 *  - currentPage   (1-based)
 *  - totalPages
 *  - onPageChange(page)
 *  - testIdPrefix  (optional)
 */
const Pagination = ({ currentPage, totalPages, onPageChange, testIdPrefix = 'pagination' }) => {
  if (totalPages <= 1) return null;

  const buildPages = () => {
    const pages = [];
    const windowSize = 1; // pages on each side of current
    const first = 1;
    const last = totalPages;

    const start = Math.max(first + 1, currentPage - windowSize);
    const end = Math.min(last - 1, currentPage + windowSize);

    pages.push(first);
    if (start > first + 1) pages.push('ellipsis-left');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < last - 1) pages.push('ellipsis-right');
    if (last !== first) pages.push(last);
    return pages;
  };

  const pages = buildPages();
  const go = (p) => {
    if (p < 1 || p > totalPages || p === currentPage) return;
    onPageChange(p);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const baseBtn = 'inline-flex items-center justify-center min-w-[36px] h-9 px-2.5 rounded-lg text-sm font-medium transition-colors border';
  const inactive = 'bg-white border-gray-200 text-charcoal hover:bg-warm-sand';
  const active = 'bg-ubud-jungle border-ubud-jungle text-white shadow-sm';
  const disabled = 'opacity-40 cursor-not-allowed';

  return (
    <nav
      className="flex items-center justify-center gap-1.5 mt-10 flex-wrap"
      data-testid={testIdPrefix}
      aria-label="Pagination"
    >
      <button
        type="button"
        className={`${baseBtn} ${inactive} ${currentPage === 1 ? disabled : ''}`}
        onClick={() => go(1)}
        disabled={currentPage === 1}
        data-testid={`${testIdPrefix}-first`}
        aria-label="First page"
      >
        <ChevronsLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={`${baseBtn} ${inactive} ${currentPage === 1 ? disabled : ''}`}
        onClick={() => go(currentPage - 1)}
        disabled={currentPage === 1}
        data-testid={`${testIdPrefix}-prev`}
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {pages.map((p) =>
        typeof p === 'string' ? (
          <span key={p} className="px-2 text-charcoal-muted text-sm select-none">…</span>
        ) : (
          <button
            key={p}
            type="button"
            className={`${baseBtn} ${p === currentPage ? active : inactive}`}
            onClick={() => go(p)}
            data-testid={`${testIdPrefix}-page-${p}`}
            aria-current={p === currentPage ? 'page' : undefined}
            aria-label={`Page ${p}`}
          >
            {p}
          </button>
        )
      )}

      <button
        type="button"
        className={`${baseBtn} ${inactive} ${currentPage === totalPages ? disabled : ''}`}
        onClick={() => go(currentPage + 1)}
        disabled={currentPage === totalPages}
        data-testid={`${testIdPrefix}-next`}
        aria-label="Next page"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
      <button
        type="button"
        className={`${baseBtn} ${inactive} ${currentPage === totalPages ? disabled : ''}`}
        onClick={() => go(totalPages)}
        disabled={currentPage === totalPages}
        data-testid={`${testIdPrefix}-last`}
        aria-label="Last page"
      >
        <ChevronsRight className="w-4 h-4" />
      </button>
    </nav>
  );
};

export default Pagination;
